import React, { useEffect, useState, useCallback, useRef } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import InViewFadeUp from "../../components/animations/InViewFadeUp";
import {
	BarChart, Bar, PieChart, Pie, Cell,
	XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
	FaHeadset, FaTicketAlt, FaCheckCircle, FaClock,
	FaChevronUp, FaChevronLeft, FaChevronRight, FaChartBar, FaChartPie,
	FaExclamationTriangle,
} from "react-icons/fa";
import { LayoutList, Reply } from "lucide-react";

import { fetchFreshserviceOverview, fetchFreshserviceAnalytics, fetchFreshserviceGroups, fetchFreshserviceReopenStats } from "../../api/overviewApi";
import { useDateFilter, rangeToParams } from "../../contexts/DateFilterContext";
import GlobalDateRangePicker from "../../components/GlobalDateRangePicker/GlobalDateRangePicker";
import ReopenedTicketsDrawer from "../../components/ReopenedTicketsDrawer/ReopenedTicketsDrawer";
import ResponseTimeDrawer from "../../components/ResponseTimeDrawer/ResponseTimeDrawer";
import AgingTicketsDrawer from "../../components/AgingTicketsDrawer/AgingTicketsDrawer";
import TicketListDrawer from "../../components/TicketListDrawer/TicketListDrawer";

// ── Color palettes ──────────────────────────────────────────────────────────
const PRIORITY_COLORS = {
	Critical: "#ef4444",
	High:     "#f97316",
	Medium:   "#f59e0b",
	Low:      "#6b7280",
};

const STATUS_COLORS = {
	Open:                  "#6f42c1",
	"In Progress":         "#3b82f6",
	Waiting:               "#f59e0b",
	"Waiting for Users":   "#f59e0b",
	"Waiting (Scheduled)": "#d97706",
	Resolved:              "#10b981",
	Closed:                "#6b7280",
	Completed:             "#6b7280",
};

const AGING_COLORS = {
	"3-7 days":   "#f59e0b",
	"7-12 days":  "#f97316",
	"12-20 days": "#ef4444",
};

const SOURCE_PALETTE = ["#6E00FF", "#2E5BFF", "#00C48C", "#FFA000", "#FF4560", "#8b5cf6", "#ec4899"];
const SOURCE_COLORS  = {
	Email:  "#6E00FF",
	Portal: "#2E5BFF",
	Phone:  "#00C48C",
	Chat:   "#FFA000",
	Walkup: "#FF4560",
};

// ── Label sanitizer ──────────────────────────────────────────────────────────
const LABEL_MAP = {
	"(account lifecycle - for hd use only)": "Account Lifecycle Management",
};
const sanitizeLabel = (label) => {
	if (!label) return "—";
	const key = label.toLowerCase().trim();
	if (LABEL_MAP[key]) return LABEL_MAP[key];
	const cleaned = label.replace(/\s*\([^)]*\)/g, "").trim();
	return cleaned || label;
};

// ── Shared tooltip ──────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
	if (!active || !payload?.length) return null;
	return (
		<div style={{
			background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
			borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 500,
			color: "var(--color-text)", boxShadow: "0 4px 16px rgba(0,0,0,0.13)",
		}}>
			{label && <div style={{ marginBottom: 4, color: "var(--color-text-light)", fontSize: 11 }}>{label}</div>}
			{payload.map((p, i) => (
				<div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
					<div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color || p.fill, flexShrink: 0 }} />
					<span>{p.name}: <strong>{p.value}{typeof p.value === "number" && p.name?.includes("%") ? "%" : ""}</strong></span>
				</div>
			))}
		</div>
	);
};

// ── Card shell ──────────────────────────────────────────────────────────────
const ChartCard = ({ title, icon: Icon, iconColor = "#6f42c1", children, minHeight = 280, delay = 0 }) => (
	<motion.div
		initial={{ opacity: 0, y: 20 }}
		animate={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.4, delay }}
		style={{ height: "100%" }}
	>
		<div style={{
			background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
			borderRadius: "var(--radius-card)", padding: 24, minHeight, height: "100%",
			boxShadow: "var(--shadow-sm)", display: "flex", flexDirection: "column",
		}}>
			<div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, minHeight: 52, flexShrink: 0 }}>
				<div style={{
					width: 32, height: 32, borderRadius: 10,
					background: `${iconColor}22`,
					display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
				}}>
					<Icon size={15} color={iconColor} />
				</div>
				<h6 className="fw-bold m-0" style={{ fontSize: 14, color: "var(--color-text)" }}>{title}</h6>
			</div>
			<div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>{children}</div>
		</div>
	</motion.div>
);

// ── Loading placeholder ─────────────────────────────────────────────────────
const ChartLoading = () => (
	<div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 160, gap: 8 }}>
		<div style={{
			width: 16, height: 16, borderRadius: "50%",
			border: "2px solid rgba(111,66,193,0.25)", borderTopColor: "#6f42c1",
			animation: "hd-spin 0.8s linear infinite",
		}} />
		<span style={{ color: "var(--color-text-light)", fontSize: 13 }}>Loading…</span>
		<style>{`@keyframes hd-spin { to { transform: rotate(360deg); } }`}</style>
	</div>
);

// ── Empty placeholder ───────────────────────────────────────────────────────
const ChartEmpty = ({ message = "No data available" }) => (
	<div style={{
		display: "flex", flexDirection: "column", alignItems: "center",
		justifyContent: "center", minHeight: 140, gap: 8,
		color: "var(--color-text-light)",
	}}>
		<FaTicketAlt size={24} style={{ opacity: 0.25 }} />
		<span style={{ fontSize: 12, textAlign: "center", maxWidth: 220 }}>{message}</span>
	</div>
);

// ── Donut chart ──────────────────────────────────────────────────────────────
const DonutChart = ({ data, colorMap, total, centerLabel }) => {
	const [activeIndex, setActiveIndex] = useState(null);
	if (!data?.length) return <ChartEmpty />;

	const chartData = data.map(d => ({
		name:  d.label,
		value: d.count,
		color: colorMap?.[d.label] || "#6b7280",
	}));

	const active    = activeIndex !== null ? chartData[activeIndex] : null;
	const activePct = active && total > 0 ? ((active.value / total) * 100).toFixed(1) : null;

	return (
		<>
			<div style={{ position: "relative", width: "100%", height: 200 }}>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={chartData}
							dataKey="value" nameKey="name"
							cx="50%" cy="50%"
							innerRadius="54%" outerRadius="78%"
							paddingAngle={3} stroke="none"
							startAngle={90} endAngle={-270}
							animationDuration={900} animationEasing="ease-out"
							onMouseEnter={(_, index) => setActiveIndex(index)}
							onMouseLeave={() => setActiveIndex(null)}
						>
							{chartData.map((entry, i) => (
								<Cell
									key={i}
									fill={entry.color}
									opacity={activeIndex === null || activeIndex === i ? 1 : 0.35}
									strokeWidth={activeIndex === i ? 3 : 0}
									stroke={activeIndex === i ? "#fff" : "none"}
									style={{ cursor: "pointer", filter: activeIndex === i ? `drop-shadow(0 0 6px ${entry.color}88)` : "none" }}
								/>
							))}
						</Pie>
					</PieChart>
				</ResponsiveContainer>

				<div style={{
					position: "absolute", top: "50%", left: "50%",
					transform: "translate(-50%, -50%)",
					textAlign: "center", pointerEvents: "none",
					transition: "all 0.15s ease",
					width: 100,
				}}>
					{active ? (
						<>
							<div style={{ fontSize: 20, fontWeight: 800, color: active.color, lineHeight: 1.1 }}>
								{active.value.toLocaleString()}
							</div>
							<div style={{ fontSize: 12, color: active.color, fontWeight: 700, marginTop: 2 }}>
								{activePct}%
							</div>
							<div style={{ fontSize: 10, color: "var(--color-text-light)", marginTop: 2, lineHeight: 1.3 }}>
								{active.name}
							</div>
						</>
					) : (
						<>
							<div style={{ fontSize: 24, fontWeight: 800, color: "var(--color-text)", lineHeight: 1.1 }}>
								{total?.toLocaleString()}
							</div>
							<div style={{ fontSize: 11, color: "var(--color-text-light)", marginTop: 4, lineHeight: 1.3 }}>
								{centerLabel}
							</div>
						</>
					)}
				</div>
			</div>

			<div style={{
				display: "flex", flexWrap: "wrap", gap: "6px 16px",
				justifyContent: "center", marginTop: 14, padding: "0 4px",
			}}>
				{chartData.map((d, i) => (
					<div
						key={i}
						style={{
							display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
							opacity: activeIndex === null || activeIndex === i ? 1 : 0.35,
							transition: "opacity 0.15s",
						}}
						onMouseEnter={() => setActiveIndex(i)}
						onMouseLeave={() => setActiveIndex(null)}
					>
						<div style={{ width: 8, height: 8, borderRadius: "50%", background: d.color, flexShrink: 0 }} />
						<span style={{ fontSize: 12, color: "var(--color-text-light)", whiteSpace: "nowrap" }}>
							<span style={{ color: "var(--color-text)", fontWeight: 600 }}>{d.name}</span>
							{" "}({d.value.toLocaleString()})
						</span>
					</div>
				))}
			</div>
		</>
	);
};

// ── Leaderboard list ────────────────────────────────────────────────────────
const LEADERBOARD_LIMIT = 7;
const LeaderboardList = ({ data, colorMap, onItemClick }) => {
	const [expanded, setExpanded] = useState(false);
	if (!data?.length) return <ChartEmpty />;
	const sorted  = [...data].sort((a, b) => b.count - a.count);
	const total   = sorted.reduce((s, d) => s + d.count, 0);
	const visible = expanded ? sorted : sorted.slice(0, LEADERBOARD_LIMIT);
	const hidden  = sorted.length - LEADERBOARD_LIMIT;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
			{visible.map((d, i) => {
				const color = colorMap?.[d.label] || "#6b7280";
				const pct   = total > 0 ? (d.count / total) * 100 : 0;
				const RowTag = onItemClick ? "button" : "div";
				return (
					<RowTag
						key={d.label}
						{...(onItemClick ? {
							onClick: () => onItemClick(d),
							"aria-label": `View ${d.label} tickets`,
							style: { border: "none", background: "transparent", padding: 0, textAlign: "left", cursor: "pointer", width: "100%" },
						} : {})}
					>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
							<span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text)" }}>{d.label}</span>
							<span style={{ fontSize: 12, fontWeight: 700, color, whiteSpace: "nowrap", marginLeft: 12 }}>
								{d.count.toLocaleString()} <span style={{ fontWeight: 400, color: "var(--color-text-light)", fontSize: 11 }}>({pct.toFixed(1)}%)</span>
							</span>
						</div>
						<div style={{ height: 7, borderRadius: 4, background: "var(--color-border)", overflow: "hidden" }}>
							<motion.div
								initial={{ width: 0 }}
								animate={{ width: `${pct}%` }}
								transition={{ duration: 0.7, ease: "easeOut", delay: i * 0.06 }}
								style={{ height: "100%", borderRadius: 4, background: color }}
							/>
						</div>
					</RowTag>
				);
			})}

			{sorted.length > LEADERBOARD_LIMIT && (
				<button
					onClick={() => setExpanded(e => !e)}
					style={{
						alignSelf: "center", marginTop: 2, padding: "5px 16px",
						fontSize: 12, fontWeight: 600, color: "var(--color-primary, #6f42c1)",
						background: "transparent", border: "1px solid var(--color-border)",
						borderRadius: 20, cursor: "pointer", transition: "background 0.15s, border-color 0.15s",
					}}
					onMouseEnter={e => { e.currentTarget.style.background = "rgba(111,66,193,0.07)"; e.currentTarget.style.borderColor = "var(--color-primary, #6f42c1)"; }}
					onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "var(--color-border)"; }}
				>
					{expanded ? "Show less" : `Show ${hidden} more`}
				</button>
			)}
		</div>
	);
};

// ── Category drill-down bar chart ───────────────────────────────────────────
// Single chart that starts at Top Categories; clicking a bar drills into its
// sub-categories, and (where the data has it, e.g. Office365) one level further
// into items. A breadcrumb + back arrow navigate back up.
const CategoryDrilldownBar = ({ label, subtitle, count, pct, max, color, drillable, onClick }) => (
	<div
		onClick={drillable ? onClick : undefined}
		onKeyDown={drillable ? (e) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				onClick(e);
			}
		} : undefined}
		role={drillable ? "button" : undefined}
		tabIndex={drillable ? 0 : undefined}
		title={label}
		style={{
			display: "flex", alignItems: "center", gap: 10,
			cursor: drillable ? "pointer" : "default", padding: "5px 0",
		}}
	>
		<div style={{ width: 230, flexShrink: 0, minWidth: 0 }}>
			<div style={{
				fontSize: 12, fontWeight: 500, color: "var(--color-text)",
				overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
			}}>
				{sanitizeLabel(label)}
			</div>
			{subtitle && (
				<div style={{
					fontSize: 10, color: "var(--color-text-light)",
					overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
				}}>
					{sanitizeLabel(subtitle)}
				</div>
			)}
		</div>
		<div style={{ flex: 1, minWidth: 0, height: 8, borderRadius: 4, background: "var(--color-border)" }}>
			<div style={{
				height: "100%", borderRadius: 4, background: color,
				width: `${Math.round((count / max) * 100)}%`,
				transition: "width 0.5s ease",
			}} />
		</div>
		<span style={{
			flexShrink: 0, width: 68, textAlign: "right",
			fontSize: 11, fontWeight: 600, color: "var(--color-text-light)", whiteSpace: "nowrap",
		}}>
			{count.toLocaleString()} · {pct}%
		</span>
		<span style={{
			flexShrink: 0, width: 12, display: "flex", alignItems: "center", justifyContent: "center",
			color: "var(--color-text-light)",
		}}>
			{drillable && <FaChevronRight size={9} />}
		</span>
	</div>
);

// How many rows are shown before the "View all" expander kicks in — applies at
// every drill-down level (top categories, sub-categories, items).
const DRILLDOWN_LIMIT = 8;

const VIEW_TABS = [
	{ key: "categories",    label: "Categories" },
	{ key: "subcategories", label: "Sub-Categories" },
];

const CategoryDrilldownChart = ({ categories, subCategories }) => {
	const [view, setView]         = useState("categories"); // "categories" | "subcategories"
	const [path, setPath]         = useState([]); // [] = categories, [cat] = sub-categories, [cat, sub] = items
	const [expanded, setExpanded] = useState(false);

	if (!categories?.length) return <ChartEmpty message="No category data available." />;

	const switchView = (v) => { setView(v); setPath([]); setExpanded(false); };

	let bars;
	if (view === "subcategories") {
		bars = (subCategories || [])
			.map(sc => ({
				key: `${sc.category}::${sc.label}`, label: sc.label, subtitle: sc.category, count: sc.count,
				drillable: false,
			}))
			.sort((a, b) => b.count - a.count);
	} else if (path.length === 0) {
		const categoriesWithSubs = new Set((subCategories || []).map(sc => sc.category));
		bars = categories.map(c => ({
			key: c.label, label: c.label, count: c.count,
			drillable: categoriesWithSubs.has(c.label),
		}));
	} else if (path.length === 1) {
		bars = (subCategories || [])
			.filter(sc => sc.category === path[0])
			.map(sc => ({
				key: sc.label, label: sc.label, count: sc.count,
				drillable: sc.items?.length > 0,
			}))
			.sort((a, b) => b.count - a.count);
	} else {
		const sc = (subCategories || []).find(s => s.category === path[0] && s.label === path[1]);
		bars = (sc?.items || []).map(it => ({ key: it.label, label: it.label, count: it.count, drillable: false }));
	}

	const total   = bars.reduce((s, d) => s + d.count, 0);
	const max     = Math.max(...bars.map(d => d.count), 1);
	const visible = expanded ? bars : bars.slice(0, DRILLDOWN_LIMIT);
	const hidden  = bars.length - DRILLDOWN_LIMIT;

	const drillInto = (bar) => { if (bar.drillable) { setPath(p => [...p, bar.label]); setExpanded(false); } };
	const jumpTo     = (depth) => { setPath(p => p.slice(0, depth)); setExpanded(false); };

	return (
		<div>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
				<div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
					{view === "categories" && path.length > 0 && (
						<button
							onClick={() => jumpTo(path.length - 1)}
							aria-label="Back"
							style={{
								display: "flex", alignItems: "center", justifyContent: "center",
								width: 24, height: 24, borderRadius: 6, border: "1px solid var(--color-border)",
								background: "transparent", color: "var(--color-text-light)", cursor: "pointer",
								flexShrink: 0, marginRight: 4,
							}}
							onMouseEnter={e => { e.currentTarget.style.background = "rgba(111,66,193,0.08)"; }}
							onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
						>
							<FaChevronLeft size={10} />
						</button>
					)}
					<button
						onClick={() => jumpTo(0)}
						style={{
							background: "none", border: "none", padding: 0,
							cursor: view === "categories" && path.length ? "pointer" : "default",
							fontSize: 12, fontWeight: view === "categories" && path.length === 0 ? 700 : 500,
							color: view === "categories" && path.length === 0 ? "var(--color-text)" : "#6f42c1",
						}}
					>
						{view === "subcategories" ? "All Sub-Categories" : "All Categories"}
					</button>
					{view === "categories" && path.map((segment, i) => (
						<React.Fragment key={i}>
							<span style={{ color: "var(--color-text-light)", fontSize: 11 }}>/</span>
							<button
								onClick={() => jumpTo(i + 1)}
								style={{
									background: "none", border: "none", padding: 0,
									cursor: i < path.length - 1 ? "pointer" : "default",
									fontSize: 12, fontWeight: i === path.length - 1 ? 700 : 500,
									color: i === path.length - 1 ? "var(--color-text)" : "#6f42c1",
								}}
							>
								{sanitizeLabel(segment)}
							</button>
						</React.Fragment>
					))}
				</div>

				<div role="group" aria-label="Category view" style={{ display: "flex", background: "var(--color-border)", borderRadius: 20, padding: 2, gap: 2, flexShrink: 0 }}>
					{VIEW_TABS.map(t => (
						<button
							key={t.key}
							onClick={() => switchView(t.key)}
							aria-pressed={view === t.key}
							style={{
								padding: "5px 14px", borderRadius: 18, border: "none", cursor: "pointer",
								fontSize: 11, fontWeight: 600, transition: "background 0.15s, color 0.15s",
								background: view === t.key ? "var(--color-bg-card)" : "transparent",
								color: view === t.key ? "#6f42c1" : "var(--color-text-light)",
								boxShadow: view === t.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
							}}
						>
							{t.label}
						</button>
					))}
				</div>
			</div>

			<AnimatePresence mode="wait">
				<motion.div
					key={`${view}:${path.join("/")}`}
					initial={{ opacity: 0, x: 16 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: -16 }}
					transition={{ duration: 0.2 }}
					style={{ display: "flex", flexDirection: "column", gap: 2 }}
				>
					{!bars.length ? (
						<ChartEmpty message="No data at this level." />
					) : (
						<>
							{visible.map((d, i) => (
								<CategoryDrilldownBar
									key={d.key}
									label={d.label} subtitle={d.subtitle} count={d.count}
									pct={total > 0 ? Math.round((d.count / total) * 100) : 0}
									max={max} drillable={d.drillable}
									color={`rgba(111,66,193,${Math.max(0.35, 0.85 - i * 0.06)})`}
									onClick={d.drillable ? () => drillInto(d) : undefined}
								/>
							))}
							{bars.length > DRILLDOWN_LIMIT && (
								<button
									onClick={() => setExpanded(e => !e)}
									style={{
										alignSelf: "center", marginTop: 10, padding: "5px 16px",
										fontSize: 12, fontWeight: 600, color: "var(--color-primary, #6f42c1)",
										background: "transparent", border: "1px solid var(--color-border)",
										borderRadius: 20, cursor: "pointer", transition: "background 0.15s, border-color 0.15s",
									}}
									onMouseEnter={e => { e.currentTarget.style.background = "rgba(111,66,193,0.07)"; e.currentTarget.style.borderColor = "var(--color-primary, #6f42c1)"; }}
									onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "var(--color-border)"; }}
								>
									{expanded ? "Show less" : `+ View all ${bars.length} (${hidden} more)`}
								</button>
							)}
						</>
					)}
				</motion.div>
			</AnimatePresence>
		</div>
	);
};

const formatResolutionTime = (valueInDays) => {
	if (valueInDays == null || Number.isNaN(valueInDays)) return "—";
	if (valueInDays < 1) {
		const hours = Math.round(valueInDays * 24);
		return `${hours}h`;
	}
	if (valueInDays < 7) {
		return `${valueInDays.toFixed(1)}d`;
	}
	const weeks = (valueInDays / 7).toFixed(1);
	return `${weeks}w`;
};

// Response times are often well under an hour, where formatResolutionTime's hour
// rounding collapses everything under 30 minutes to "0h" — show minutes instead.
const formatResponseTime = (valueInDays) => {
	if (valueInDays == null || Number.isNaN(valueInDays)) return "—";
	const totalMinutes = valueInDays * 24 * 60;
	if (totalMinutes < 60) {
		return `${Math.round(totalMinutes)}m`;
	}
	return formatResolutionTime(valueInDays);
};

// Which direction counts as "good" for a metric — trends only turn green/red when
// they move toward it; movement the wrong way is red, and metrics with no clear
// good direction (e.g. In Progress) stay neutral gray regardless of direction.
const renderTrendChip = (trend, goodDirection = null) => {
	if (!trend) return null;
	const color = trend.direction === "flat" || trend.direction === "new" || !goodDirection
		? "#6b7280"
		: trend.direction === goodDirection ? "#16a34a" : "#dc2626";
	return (
		<span style={{
			display: "inline-flex", alignItems: "center", justifyContent: "center",
			gap: 4, fontSize: 10, fontWeight: 700, color,
			whiteSpace: "nowrap", lineHeight: 1.2,
		}}>
			<span>{trend.label}</span>
		</span>
	);
};

// ── SLA grouped bars ────────────────────────────────────────────────────────
const SLA_BARS = [
	{ key: "within_24h", label: "Within 24h",    color: "#10b981" },
	{ key: "within_3d",  label: "Within 3 days", color: "#6f42c1" },
	{ key: "within_7d",  label: "Within 7 days", color: "#3b82f6" },
];

const SlaTooltip = ({ active, payload, label }) => {
	if (!active || !payload?.length) return null;
	return (
		<div style={{
			background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
			borderRadius: 8, padding: "10px 14px", fontSize: 12,
			color: "var(--color-text)", boxShadow: "0 4px 16px rgba(0,0,0,0.13)",
		}}>
			<div style={{ marginBottom: 6, fontWeight: 700, color: "var(--color-text-light)", fontSize: 11 }}>{label}</div>
			{[...payload].reverse().map((p, i) => (
				<div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
					<div style={{ width: 8, height: 8, borderRadius: 2, background: p.fill, flexShrink: 0 }} />
					<span style={{ flex: 1 }}>{p.name}</span>
					<strong>{p.value}%</strong>
				</div>
			))}
		</div>
	);
};

// Two series: tickets resolved normally, vs. tickets that sat in a
// "Waiting until a specific date" / "Waiting for equipment return" status at some
// point (Freshservice pauses its own SLA clock for those, so they're reported
// separately rather than skewing the normal average).
const RESOLUTION_BARS = [
	{ key: "avg_days",           countKey: "count",           label: "Normal",              color: "#6f42c1" },
	{ key: "scheduled_avg_days", countKey: "scheduled_count", label: "Waiting (Scheduled)", color: "#d97706" },
];

const ResolutionByPriorityTooltip = ({ active, payload, label }) => {
	if (!active || !payload?.length) return null;
	return (
		<div style={{
			background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
			borderRadius: 8, padding: "9px 13px", fontSize: 12,
			color: "var(--color-text)", boxShadow: "0 4px 16px rgba(0,0,0,0.13)",
		}}>
			<div style={{ marginBottom: 3, fontWeight: 700, color: "var(--color-text-light)", fontSize: 11 }}>{label}</div>
			{payload.map((p) => {
				const bar   = RESOLUTION_BARS.find(b => b.key === p.dataKey);
				const count = p.payload[bar?.countKey] || 0;
				return (
					<div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<div style={{ width: 8, height: 8, borderRadius: "50%", background: p.fill, flexShrink: 0 }} />
						<span>{bar?.label}: {formatResolutionTime(p.value)} <span style={{ color: "var(--color-text-light)" }}>({count.toLocaleString()} tickets)</span></span>
					</div>
				);
			})}
		</div>
	);
};

// A missing status (network failure) or 5xx is plausibly transient; a 4xx means the
// request itself is wrong (bad client_id, auth) and will fail identically every time —
// retrying it wastes a request during the exact window when retrying can't help.
const isRetryableGroupsStatus = (status) => status === null || status >= 500;

// A transient failure shouldn't be enough to fall back to unscoped analytics for a
// client that most likely does have a real Helpdesk Team group — one retry (after a
// short delay, so it isn't hammering the same brief blip immediately) turns most of
// those into a non-issue. `signal` lets an aborted effect (client switched again
// mid-retry) skip the second request entirely rather than firing it for nothing.
const fetchFreshserviceGroupsWithRetry = async (clientId, signal) => {
	const first = await fetchFreshserviceGroups(clientId, signal);
	if (first.groups !== null || !isRetryableGroupsStatus(first.status)) return first.groups;

	await new Promise(resolve => setTimeout(resolve, 400));
	if (signal?.aborted) return null;

	const second = await fetchFreshserviceGroups(clientId, signal);
	return second.groups;
};

// ══════════════════════════════════════════════════════════════════════════════
// Main page
// ══════════════════════════════════════════════════════════════════════════════
const HelpdeskPage = ({ clientId, selectedClient }) => {
	const [overview,         setOverview]         = useState(null);
	const [analytics,        setAnalytics]        = useState(null);
	const [reopenStats,      setReopenStats]      = useState(null);
	const [loadingOverview,  setLoadingOverview]  = useState(true);
	const [loadingAnalytics, setLoadingAnalytics] = useState(true);
	const [loadingReopen,    setLoadingReopen]    = useState(true);
	const [helpdeskGroupId,  setHelpdeskGroupId]  = useState(null);
	const [groupsLoaded,     setGroupsLoaded]     = useState(false);
	const [groupsFetchFailed, setGroupsFetchFailed] = useState(false);
	const [groupsRetryToken, setGroupsRetryToken] = useState(0);
	// Guards against the same stale-response race as the group lookup: a superseded
	// loadData call (e.g. a fast client switch) must not overwrite the current client's
	// state with an older client's tickets once it finally resolves.
	const loadDataRequestId = useRef(0);
	const [isReopenedDrawerOpen, setIsReopenedDrawerOpen] = useState(false);
	const [isOpenDrawerOpen, setIsOpenDrawerOpen] = useState(false);
	const [isResponseDrawerOpen, setIsResponseDrawerOpen] = useState(false);
	const [agingDrawerBucket, setAgingDrawerBucket] = useState(null);
	const [priorityDrawerBucket, setPriorityDrawerBucket] = useState(null);
	const [resolutionPriorityDrawer, setResolutionPriorityDrawer] = useState(null);
	const [reopenMonthDrawer, setReopenMonthDrawer] = useState(null);

	const { globalRange, globalCustomRange } = useDateFilter();
	const effectiveRange = globalRange;

	const clientName = selectedClient?.client_name || null;

	useEffect(() => {
		const controller = new AbortController();
		setGroupsLoaded(false);
		setHelpdeskGroupId(null);
		setGroupsFetchFailed(false);
		// Clear the previous client's data and show loading immediately on a client
		// switch — loadData won't fire its own fetch until groupsLoaded flips back to
		// true, and until it does, the old numbers (with loading flags already false
		// from their last successful load) would otherwise sit on screen under the new
		// client's name with no indication anything is stale.
		setOverview(null);
		setAnalytics(null);
		setReopenStats(null);
		setLoadingOverview(true);
		setLoadingAnalytics(true);
		setLoadingReopen(true);
		if (!clientId) {
			setLoadingOverview(false);
			setLoadingAnalytics(false);
			setLoadingReopen(false);
			return;
		}
		fetchFreshserviceGroupsWithRetry(clientId, controller.signal).then(groups => {
			if (controller.signal.aborted) return;
			if (groups === null) {
				setGroupsFetchFailed(true);
				return;
			}
			const hdGroup = groups.find(g => g.name?.toLowerCase() === 'helpdesk team');
			setHelpdeskGroupId(hdGroup?.id ?? null);
		}).catch(error => {
			if (controller.signal.aborted || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
			// Belt-and-suspenders alongside the Array.isArray guard in fetchFreshserviceGroups —
			// any unexpected throw here must still surface as a failure, not the default
			// (unset) state that would render the false-confidence "no scoping" banner.
			console.error('Error resolving Helpdesk Team group:', error);
			setGroupsFetchFailed(true);
		}).finally(() => {
			if (!controller.signal.aborted) setGroupsLoaded(true);
		});
		// A fast client switch (or a manual retry) can leave this fetch resolving after a
		// newer one already started — aborting the request outright (rather than just
		// ignoring its result) avoids wasting a call for a client no longer being viewed,
		// and since group ids are workspace-local, a stale result isn't just outdated, it's
		// meaningless in the new client's workspace, so it must never be applied either way.
		return () => { controller.abort(); };
	}, [clientId, groupsRetryToken]);

	const loadData = useCallback(async () => {
		// Must be the very first thing this callback does — every invocation, including
		// ones that bail out below, has to supersede whatever request came before it.
		// Incrementing after an early return (e.g. the !groupsLoaded bail-out a client
		// switch synchronously triggers) would let that earlier in-flight request keep
		// matching the ref and get applied once it finally resolves.
		const requestId = ++loadDataRequestId.current;

		if (!clientId) {
			setLoadingOverview(false);
			setLoadingAnalytics(false);
			setLoadingReopen(false);
			return;
		}
		// Wait for the Helpdesk Team group lookup before fetching — otherwise this
		// fires once with no group filter (all groups) and again once the group id
		// resolves, and the page briefly shows the wrong (unfiltered) numbers.
		if (!groupsLoaded) return;

		setLoadingOverview(true);
		setLoadingAnalytics(true);
		setLoadingReopen(true);

		const dateParams = rangeToParams(effectiveRange, effectiveRange === "custom" ? globalCustomRange : null);
		const [ovResult, anResult, rnResult] = await Promise.all([
			fetchFreshserviceOverview(clientId, dateParams, helpdeskGroupId),
			fetchFreshserviceAnalytics(clientId, dateParams, helpdeskGroupId),
			fetchFreshserviceReopenStats(clientId, dateParams, helpdeskGroupId),
		]);

		// A newer loadData call started (client switch, date-range change, etc.) while
		// this one was in flight — its results are for a state that's no longer current.
		if (requestId !== loadDataRequestId.current) return;

		setOverview(ovResult);
		setLoadingOverview(false);
		setAnalytics(anResult);
		setLoadingAnalytics(false);
		setReopenStats(rnResult);
		setLoadingReopen(false);
	}, [clientId, effectiveRange, globalCustomRange, helpdeskGroupId, groupsLoaded]);

	useEffect(() => { loadData(); }, [loadData]);

	const summary       = overview?.summary       || {};
	const byPriority    = overview?.by_priority   || [];

	// Status Distribution folds "Resolved" into "Closed" — for this leaderboard,
	// both mean the ticket is done, and Freshservice auto-closes resolved tickets
	// within 48h anyway, so splitting them just adds noise.
	const rawByStatus = overview?.by_status || [];
	const resolvedStatusEntry = rawByStatus.find(s => s.label === "Resolved");
	const closedStatusEntry   = rawByStatus.find(s => s.label === "Closed");
	const byStatus = resolvedStatusEntry
		? [
			...rawByStatus.filter(s => s.label !== "Resolved" && s.label !== "Closed"),
			{ label: "Completed", count: (closedStatusEntry?.count || 0) + resolvedStatusEntry.count },
		]
		: rawByStatus;

	const avgResponseValue = summary?.avg_response_days != null ? summary.avg_response_days : null;
	const avgResponseLabel = avgResponseValue != null ? formatResponseTime(avgResponseValue) : "—";
	const avgResponseTrend = summary?.avg_response_trend || null;
	const medianResponseValue = summary?.median_response_days != null ? summary.median_response_days : null;
	const medianResponseLabel = medianResponseValue != null ? formatResponseTime(medianResponseValue) : null;

	// ── Open = Open + In Progress + Waiting ─────────────────────────────────
	const inProgressCount = summary.in_progress ?? 0;
	const waitingCount    = summary.waiting     ?? 0;
	const openCount       = (summary.open ?? 0) + inProgressCount + waitingCount;

	// ── Completed = Resolved + Closed ───────────────────────────────────────
	const resolvedCount  = summary.resolved ?? 0;
	const closedCount    = summary.closed   ?? 0;
	const completedCount = resolvedCount + closedCount;

	// ── Reopen stats ─────────────────────────────────────────────────────────
	const reopenCount   = reopenStats?.summary?.reopen_count    ?? 0;
	const reopenTrend   = reopenStats?.summary?.reopen_trend    ?? null;
	const reopenRatePct = reopenStats?.summary?.reopen_rate_pct ?? 0;

	const sourceColorMap = Object.fromEntries(
		(analytics?.by_source || []).map((d, i) => [
			d.label,
			SOURCE_COLORS[d.label] ?? SOURCE_PALETTE[i % SOURCE_PALETTE.length],
		])
	);
	const totalSource = (analytics?.by_source || []).reduce((s, d) => s + d.count, 0);

	const monthlyVol = analytics?.monthly_volume || [];
	const maxVol     = monthlyVol.length ? Math.max(...monthlyVol.map(d => d.count)) : 0;
	const slaData    = analytics?.resolution_sla || [];
	const monthlyReopens = reopenStats?.monthly_reopens || [];
	const resolutionByPriority = analytics?.resolution_by_priority || [];
	const agingBuckets = analytics?.aging_buckets || [];

	return (
		<Container fluid className="py-3">

			{/* ── HEADER ─────────────────────────────────────────────────── */}
			<motion.div
				initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
				className="mb-4" style={{ marginTop: "-1.5rem" }}
			>
				<Card style={{
					borderRadius: 12,
					background: (() => {
						const isDark = document.body.classList.contains("dark-mode");
						return isDark
							? "linear-gradient(135deg, rgba(111,66,193,0.2) 0%, rgba(139,92,246,0.15) 100%)"
							: "linear-gradient(135deg, rgba(111,66,193,0.1) 0%, rgba(139,92,246,0.08) 100%)";
					})(),
					border: "1px solid var(--color-border)",
					boxShadow: "var(--shadow-sm)",
				}}>
					<Card.Body className="p-3">
						<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
							<div className="d-flex align-items-center gap-3">
								<div style={{ background: "rgba(111,66,193,0.15)", padding: 10, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
									<FaHeadset size={20} color="#6f42c1" />
								</div>
								<div>
									<h4 className="fw-bold mb-0" style={{ fontSize: 18, color: "var(--color-text)" }}>
										Helpdesk{clientName && <span style={{ color: "#6f42c1", marginLeft: 8 }}>{clientName}</span>}
									</h4>
									<p className="mb-0" style={{ fontSize: 12, color: "var(--color-text-light)" }}>
										Freshservice ticket analytics
										{groupsLoaded && !helpdeskGroupId && (
											groupsFetchFailed ? (
												// Deliberately styled as an error (not the informational amber below) —
												// this means the numbers on screen may be wrong, not just unscoped by
												// design, and reads that way rather than blending into a routine note.
												<span style={{ marginLeft: 8, color: "#ef4444" }}>
													— couldn't load team scoping, showing all tickets.{" "}
													<button
														type="button"
														onClick={() => setGroupsRetryToken(t => t + 1)}
														style={{
															background: "none", border: "none", padding: 0,
															color: "#ef4444", textDecoration: "underline",
															cursor: "pointer", font: "inherit",
														}}
													>
														Retry
													</button>
												</span>
											) : (
												<span style={{ marginLeft: 8, color: "#f59e0b" }}>
													— showing all tickets (no Helpdesk Team scoping available for this client)
												</span>
											)
										)}
									</p>
								</div>
							</div>
							<div style={{ flexShrink: 0 }}>
								<GlobalDateRangePicker />
							</div>
						</div>
					</Card.Body>
				</Card>
			</motion.div>

			{/* ── KPI STAT BAR ───────────────────────────────────────────── */}
			<InViewFadeUp delay={0.1}>
				<div style={{
					display: "flex", alignItems: "stretch",
					background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
					borderRadius: "var(--radius-card)", boxShadow: "var(--shadow-sm)",
					overflowX: "auto", overflowY: "hidden", flexWrap: "nowrap", marginBottom: 20,
				}}>
					{[
						{
							key: "open", label: "Open",
							value: openCount,
							iconBg: "rgba(59,130,246,0.12)", iconColor: "#3b82f6",
							Icon: LayoutList, trend: summary.open_trend, goodDirection: "down",
							subtitle: `${inProgressCount.toLocaleString()} In Progress · ${waitingCount.toLocaleString()} Waiting`,
							onClick: () => setIsOpenDrawerOpen(true),
							ariaLabel: "View open tickets",
						},
						{
							key: "completed", label: "Completed",
							value: completedCount,
							iconBg: "rgba(16,185,129,0.12)", iconColor: "#10b981",
							Icon: FaCheckCircle, trend: summary.resolved_trend, goodDirection: "up",
							subtitle: `${resolvedCount.toLocaleString()} Resolved · ${closedCount.toLocaleString()} Closed`,
						},
						{
							key: "reopened", label: "Reopened",
							value: reopenCount,
							iconBg: "rgba(239,68,68,0.12)", iconColor: "#ef4444",
							Icon: FaChevronUp, trend: reopenTrend, goodDirection: "down",
							subtitle: `${reopenRatePct}% reopen rate`,
							onClick: () => setIsReopenedDrawerOpen(true),
							ariaLabel: "View reopened tickets",
							loading: loadingReopen,
						},
						{
							key: "avg_response", label: "Avg. Response",
							value: avgResponseLabel,
							iconBg: "rgba(99,102,241,0.12)", iconColor: "#6366f1",
							Icon: Reply, trend: avgResponseTrend, goodDirection: "down",
							subtitle: medianResponseLabel ? `Median: ${medianResponseLabel}` : undefined,
							onClick: () => setIsResponseDrawerOpen(true),
							ariaLabel: "View slowest first-response tickets",
						},
					].map(({ label, value, iconBg, iconColor, Icon, trend, goodDirection, subtitle, onClick, ariaLabel, loading }, i, arr) => {
						const isClickable = Boolean(onClick);
						const isLoading = loading != null ? loading : loadingOverview;
						const CardTag = isClickable ? "button" : "div";
						return (
						<React.Fragment key={label}>
							<CardTag
								{...(isClickable ? {
									onClick,
									"aria-label": ariaLabel,
									onMouseEnter: (e) => { e.currentTarget.style.borderColor = iconColor; e.currentTarget.style.background = `${iconColor}0A`; },
									onMouseLeave: (e) => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.background = "transparent"; },
									onFocus: (e) => { e.currentTarget.style.borderColor = iconColor; },
									onBlur: (e) => { e.currentTarget.style.borderColor = "transparent"; },
								} : {})}
								style={{
									flex: 1, flexBasis: 0, minWidth: 160,
									display: "flex", flexDirection: "column",
									justifyContent: "space-between", alignItems: "center",
									padding: "16px 10px", gap: 10,
									boxSizing: "border-box", minHeight: 190, textAlign: "center",
									...(isClickable ? {
										border: "1px solid transparent", borderRadius: 10,
										background: "transparent", font: "inherit", cursor: "pointer",
										transition: "border-color 0.15s, background 0.15s",
									} : {}),
								}}
							>
								<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, width: "100%" }}>
									<div style={{
										width: 40, height: 40, borderRadius: 12, background: iconBg,
										display: "flex", alignItems: "center", justifyContent: "center",
									}}>
										<Icon size={16} color={iconColor} />
									</div>
									<div style={{
										fontSize: 10, fontWeight: 600, color: "var(--color-text-light)",
										textTransform: "uppercase", letterSpacing: "0.08em",
									}}>
										{label}
									</div>
									<div style={{
										fontSize: 34, fontWeight: 800, color: iconColor,
										lineHeight: 1.05, letterSpacing: "-0.03em", wordBreak: "break-word",
									}}>
										{isLoading
										? "—"
										: typeof value === "number" ? value.toLocaleString() : value
									}
									</div>
									{subtitle && (
										<div style={{ fontSize: 10, color: "var(--color-text-light)", marginTop: -4, lineHeight: 1.4 }}>
											{subtitle}
										</div>
									)}
								</div>
								{/* Trend chips temporarily hidden — re-enable by restoring this block. */}
								{false && (
									<div style={{ width: "100%", marginTop: 6, display: "flex", justifyContent: "center" }}>
										{renderTrendChip(trend, goodDirection)}
									</div>
								)}
							</CardTag>
							{i < arr.length - 1 && <div style={{ width: 1, background: "var(--color-border)", flexShrink: 0 }} />}
						</React.Fragment>
						);
					})}
				</div>
			</InViewFadeUp>

			{/* ── ROW 1: Monthly Volume + Source Breakdown ────────────────── */}
			<Row className="g-3 mb-4">
				<Col md={7}>
					<ChartCard title="Monthly Ticket Volume" icon={FaChartBar} delay={0.1}>
						{loadingAnalytics ? <ChartLoading /> : !monthlyVol.length
							? <ChartEmpty message="No monthly volume data available." />
							: (
								<div style={{ width: "100%", height: 220 }}>
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={monthlyVol} barSize={34}>
											<defs>
												<linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
													<stop offset="0%" stopColor="#8b5cf6" />
													<stop offset="100%" stopColor="#6f42c1" />
												</linearGradient>
												<linearGradient id="peakGrad" x1="0" y1="0" x2="0" y2="1">
													<stop offset="0%" stopColor="#34d399" />
													<stop offset="100%" stopColor="#10b981" />
												</linearGradient>
											</defs>
											<CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
											<XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--color-text-light)" }} axisLine={false} tickLine={false} />
											<YAxis tick={{ fontSize: 11, fill: "var(--color-text-light)" }} axisLine={false} tickLine={false} width={32} />
											<Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(111,66,193,0.06)" }} />
											<Bar dataKey="count" name="Tickets" radius={[6, 6, 0, 0]}>
												{monthlyVol.map((entry, i) => (
													<Cell key={i} fill={entry.count === maxVol ? "url(#peakGrad)" : "url(#volGrad)"} />
												))}
											</Bar>
										</BarChart>
									</ResponsiveContainer>
								</div>
							)
						}
					</ChartCard>
				</Col>
				<Col md={5}>
					<ChartCard title="Ticket Source" icon={FaChartPie} delay={0.15} minHeight={0}>
						{loadingAnalytics ? <ChartLoading /> : !analytics?.by_source?.length
							? <ChartEmpty message="No source data available." />
							: <DonutChart
								data={analytics.by_source}
								colorMap={sourceColorMap}
								total={totalSource}
								centerLabel="Total"
							/>
						}
					</ChartCard>
				</Col>
			</Row>

			{/* ── ROW 2: Status + Priority leaderboards ───────────────────── */}
			<Row className="g-3 mb-4">
				<Col md={6}>
					<ChartCard title="Status Distribution" icon={LayoutList} delay={0.2} minHeight={0}>
						{loadingOverview ? <ChartLoading /> : !byStatus.length ? <ChartEmpty /> : (
							<LeaderboardList data={byStatus} colorMap={STATUS_COLORS} />
						)}
					</ChartCard>
				</Col>
				<Col md={6}>
					<ChartCard title="Priority Distribution" icon={FaExclamationTriangle} iconColor="#f59e0b" delay={0.25} minHeight={0}>
						{loadingOverview ? <ChartLoading /> : !byPriority.length ? <ChartEmpty /> : (
							<LeaderboardList data={byPriority} colorMap={PRIORITY_COLORS} onItemClick={setPriorityDrawerBucket} />
						)}
					</ChartCard>
				</Col>
			</Row>

			{/* ── ROW 3: Resolution SLA ────────────────────────────────────── */}
			<Row className="g-3 mb-4 align-items-stretch">
				<Col md={8}>
					<ChartCard title="Resolution SLA — % Resolved Within Target (by Month)" icon={FaClock} delay={0.3} minHeight={260}>
						{loadingAnalytics ? <ChartLoading /> : !slaData.length
							? <ChartEmpty message="No resolution SLA data available." />
							: (
								<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
									<div style={{ width: "100%", height: 220, flexShrink: 0 }}>
										<ResponsiveContainer width="100%" height="100%">
											<BarChart data={slaData} barSize={14} barGap={2} barCategoryGap="30%">
												<CartesianGrid strokeDasharray="4 4" stroke="rgba(0,0,0,0.06)" vertical={false} />
												<XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--color-text-light)" }} axisLine={false} tickLine={false} />
												<YAxis tick={{ fontSize: 11, fill: "var(--color-text-light)" }} axisLine={false} tickLine={false} width={32} unit="%" domain={[0, 100]} />
												<Tooltip content={<SlaTooltip />} cursor={{ fill: "rgba(111,66,193,0.04)" }} />
												{SLA_BARS.map(b => (
													<Bar key={b.key} dataKey={b.key} name={`${b.label} %`} fill={b.color} radius={[4, 4, 0, 0]} minPointLength={2} />
												))}
											</BarChart>
										</ResponsiveContainer>
									</div>
									<div style={{ display: "flex", gap: 20, marginTop: 10, justifyContent: "center", flexWrap: "wrap", flexShrink: 0 }}>
										{SLA_BARS.map(b => (
											<div key={b.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
												<div style={{ width: 10, height: 10, borderRadius: 2, background: b.color, flexShrink: 0 }} />
												<span style={{ fontSize: 11, color: "var(--color-text-light)" }}>{b.label}</span>
											</div>
										))}
									</div>
								</div>
							)
						}
					</ChartCard>
				</Col>
				<Col md={4}>
					<ChartCard title="Aging Tickets — Open Tickets by Age" icon={FaClock} iconColor="#f97316" delay={0.33} minHeight={260}>
						{loadingAnalytics ? <ChartLoading /> : !agingBuckets.some(b => b.count > 0)
							? <ChartEmpty message="No open tickets aging 3-20 days in this period." />
							: (
								<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
									<div style={{ width: "100%", height: 220, flexShrink: 0 }}>
										<ResponsiveContainer width="100%" height="100%">
											<BarChart data={agingBuckets} barSize={40}>
												<CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
												<XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--color-text-light)" }} axisLine={false} tickLine={false} />
												<YAxis tick={{ fontSize: 11, fill: "var(--color-text-light)" }} axisLine={false} tickLine={false} width={32} allowDecimals={false} />
												<Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(249,115,22,0.06)" }} />
												<Bar dataKey="count" name="Open Tickets" radius={[6, 6, 0, 0]} cursor="pointer" onClick={(data) => setAgingDrawerBucket(data)}>
													{agingBuckets.map((b, i) => (
														<Cell key={i} fill={AGING_COLORS[b.label] || "#f97316"} />
													))}
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
									{/* Spacer matching the SLA card's legend row height, so both chart canvases end at the same Y */}
									<div style={{ height: 30, marginTop: 10, flexShrink: 0 }} />
								</div>
							)
						}
					</ChartCard>
				</Col>
			</Row>

			{/* ── ROW 3A: Resolution Time by Priority + Reopen Trend ────────── */}
			<Row className="g-3 mb-4">
				<Col md={6}>
					<ChartCard title="Avg. Resolution Time by Priority" icon={FaClock} delay={0.31} minHeight={220}>
						{loadingAnalytics ? <ChartLoading /> : !resolutionByPriority.length
							? <ChartEmpty message="No resolved tickets with priority data in this period." />
							: (
								<>
									<div style={{ width: "100%", height: 200 }}>
										<ResponsiveContainer width="100%" height="100%">
											<BarChart data={resolutionByPriority} barSize={18} barGap={2} barCategoryGap="30%">
												<CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
												<XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--color-text-light)" }} axisLine={false} tickLine={false} />
												<YAxis
													tick={{ fontSize: 11, fill: "var(--color-text-light)" }} axisLine={false} tickLine={false} width={40}
													tickFormatter={(v) => formatResolutionTime(v)}
												/>
												<Tooltip content={<ResolutionByPriorityTooltip />} cursor={{ fill: "rgba(111,66,193,0.06)" }} />
												{RESOLUTION_BARS.map(b => (
													<Bar
														key={b.key} dataKey={b.key} name={b.label} fill={b.color} radius={[4, 4, 0, 0]}
														cursor="pointer" onClick={(data) => setResolutionPriorityDrawer(data)}
													/>
												))}
											</BarChart>
										</ResponsiveContainer>
									</div>
									<div style={{ display: "flex", gap: 20, marginTop: 10, justifyContent: "center", flexWrap: "wrap" }}>
										{RESOLUTION_BARS.map(b => (
											<div key={b.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
												<div style={{ width: 10, height: 10, borderRadius: 2, background: b.color, flexShrink: 0 }} />
												<span style={{ fontSize: 11, color: "var(--color-text-light)" }}>{b.label}</span>
											</div>
										))}
									</div>
								</>
							)
						}
					</ChartCard>
				</Col>
				<Col md={6}>
					<ChartCard title="Reopen Trend (by Month)" icon={FaChevronUp} iconColor="#ef4444" delay={0.32} minHeight={220}>
						{loadingReopen ? <ChartLoading /> : !monthlyReopens.length
							? <ChartEmpty message="No reopened tickets in this period." />
							: (
								<div style={{ width: "100%", height: 200 }}>
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={monthlyReopens} barSize={28}>
											<CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
											<XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--color-text-light)" }} axisLine={false} tickLine={false} />
											<YAxis tick={{ fontSize: 11, fill: "var(--color-text-light)" }} axisLine={false} tickLine={false} width={32} allowDecimals={false} />
											<Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(239,68,68,0.06)" }} />
											<Bar
												dataKey="count" name="Reopens" fill="#ef4444" radius={[6, 6, 0, 0]}
												cursor="pointer" onClick={(data) => setReopenMonthDrawer(data)}
											/>
										</BarChart>
									</ResponsiveContainer>
								</div>
							)
						}
					</ChartCard>
				</Col>
			</Row>

			{/* ── ROW 4: Categories (drill-down: category → sub-category → item) ── */}
			<Row className="g-3 mb-4">
				<Col xs={12}>
					<ChartCard title="Ticket Categories" icon={FaChartBar} delay={0.35} minHeight={300}>
						{loadingAnalytics ? <ChartLoading /> : (
							<CategoryDrilldownChart
								categories={analytics?.by_category || []}
								subCategories={analytics?.by_sub_category || []}
							/>
						)}
					</ChartCard>
				</Col>
			</Row>

			<TicketListDrawer
				open={isOpenDrawerOpen}
				onClose={() => setIsOpenDrawerOpen(false)}
				title="Open Tickets"
				subtitle="Open, In Progress, or Waiting tickets, oldest first"
				tickets={overview?.open_tickets}
				totalCount={openCount}
				priorityColors={PRIORITY_COLORS}
				statusColors={STATUS_COLORS}
				accentColor="#3b82f6"
				emptyMessage="No open tickets in this period."
			/>

			<ReopenedTicketsDrawer
				open={isReopenedDrawerOpen}
				onClose={() => setIsReopenedDrawerOpen(false)}
				tickets={reopenStats?.recent_tickets}
				totalReopenCount={reopenCount}
				priorityColors={PRIORITY_COLORS}
				statusColors={STATUS_COLORS}
			/>

			<ResponseTimeDrawer
				open={isResponseDrawerOpen}
				onClose={() => setIsResponseDrawerOpen(false)}
				tickets={overview?.response_time_tickets}
				priorityColors={PRIORITY_COLORS}
				statusColors={STATUS_COLORS}
			/>

			<AgingTicketsDrawer
				open={!!agingDrawerBucket}
				onClose={() => setAgingDrawerBucket(null)}
				bucket={agingDrawerBucket}
				priorityColors={PRIORITY_COLORS}
				statusColors={STATUS_COLORS}
				accentColor={agingDrawerBucket ? (AGING_COLORS[agingDrawerBucket.label] || "#f97316") : "#f97316"}
			/>

			<TicketListDrawer
				open={!!priorityDrawerBucket}
				onClose={() => setPriorityDrawerBucket(null)}
				title={`${priorityDrawerBucket?.label || ""} Priority`}
				subtitle="Tickets at this priority, most recently updated first"
				tickets={priorityDrawerBucket?.tickets}
				totalCount={priorityDrawerBucket?.count}
				priorityColors={PRIORITY_COLORS}
				statusColors={STATUS_COLORS}
				accentColor={priorityDrawerBucket ? (PRIORITY_COLORS[priorityDrawerBucket.label] || "#f59e0b") : "#f59e0b"}
				emptyMessage="No tickets at this priority in this period."
			/>

			<TicketListDrawer
				open={!!resolutionPriorityDrawer}
				onClose={() => setResolutionPriorityDrawer(null)}
				title={`${resolutionPriorityDrawer?.label || ""} — Resolution Time`}
				subtitle="Resolved tickets at this priority, slowest first"
				tickets={resolutionPriorityDrawer?.tickets}
				totalCount={resolutionPriorityDrawer?.count}
				priorityColors={PRIORITY_COLORS}
				statusColors={STATUS_COLORS}
				accentColor="#6f42c1"
				emptyMessage="No resolved tickets at this priority in this period."
				dateLabel="Resolved"
				dateField="updated_at"
				renderBadge={(t) => (
					<span style={{
						fontSize: 10, fontWeight: 700, color: "#6f42c1",
						background: "rgba(111,66,193,0.1)", borderRadius: 4, padding: "1px 6px",
					}}>
						{formatResolutionTime(t.resolution_time_hours / 24)} to resolve
					</span>
				)}
			/>

			<TicketListDrawer
				open={!!reopenMonthDrawer}
				onClose={() => setReopenMonthDrawer(null)}
				title={`Reopened — ${reopenMonthDrawer?.month || ""}`}
				subtitle="Tickets reopened in this month, most recently reopened first"
				tickets={reopenMonthDrawer?.tickets}
				totalCount={reopenMonthDrawer?.ticket_count}
				priorityColors={PRIORITY_COLORS}
				statusColors={STATUS_COLORS}
				accentColor="#ef4444"
				emptyMessage="No tickets reopened in this month."
				dateLabel="Last reopened"
				dateField="reopened_at"
				renderBadge={(t) => t.reopen_count > 1 ? (
					<span style={{
						fontSize: 10, fontWeight: 700, color: "#ef4444",
						background: "rgba(239,68,68,0.1)", borderRadius: 4, padding: "1px 6px",
					}}>
						Reopened {t.reopen_count}×
					</span>
				) : null}
			/>
		</Container>
	);
};

export default HelpdeskPage;